import { Link } from 'react-router-dom'
import { api } from '../../api/public'
import { useApi } from '../../components/useApi'

/**
 * Links to every season the league has played (FR-002).
 *
 * # Completed seasons stay browsable, and something has to lead to them
 *
 * The API has served the season list since Phase 13, and the standings and
 * schedule views defaulted to the current one with no way to reach any other —
 * so a finished season was reachable only by typing a URL nobody is given.
 * FR-002 says they stay browsable; a route that exists and is unreachable does
 * not satisfy that.
 *
 * # Links, not a `<select>`
 *
 * A select is an `<input>`, and every public view is asserted to carry none —
 * the check that no view offers a control to change anything (FR-047c). Links
 * keep that assertion meaningful instead of forcing an exception into it, and
 * they give each season its own URL, which is what a person shares.
 */
export function SeasonSwitcher({
  current,
  view,
}: {
  current: number
  view: 'standings' | 'schedule' | 'bracket' | 'leaders'
}) {
  const seasons = useApi((s) => api.seasons(s), [])
  if (seasons.state !== 'ready' || seasons.data.length < 2) return null

  return (
    <nav className="season-switcher" aria-label="Seasons">
      {seasons.data.map((s) => (
        <Link
          key={s.id}
          to={`/seasons/${s.id}/${view}`}
          className={s.id === current ? 'current' : undefined}
          aria-current={s.id === current ? 'page' : undefined}
        >
          {s.name}
          {s.is_current && <span className="badge badge-in_progress">Now</span>}
        </Link>
      ))}
    </nav>
  )
}
